import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import CropCalendar from "@/components/CropCalendar";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/05a985b6-8cfb-46a2-85d5-dffa8a2df5d1/images/f2f3397c982fd9a70d4eb9912ad7bcf07201efbf26e8066c8185cef00db4f301.jpeg";

const STATS = [
  { k: "99.5%", l: "Grain Purity" },
  { k: "Premium", l: "Quality Grade" },
  { k: "Certified", l: "Organic Options" },
  { k: "MT/Mo", l: "Capacity 50,000" },
];

const CATEGORIES = [
  {
    title: "Basmati Rice",
    items: ["Super Basmati", "Long grain", "Aromatic", "Aged"],
    img: "https://images.pexels.com/photos/7363674/pexels-photo-7363674.jpeg?auto=compress&cs=tinysrgb&w=900&h=675&fit=crop",
  },
  {
    title: "White & Brown Rice",
    items: ["Polished white", "Whole grain brown", "Multiple grades"],
    img: "https://images.pexels.com/photos/1059943/pexels-photo-1059943.jpeg?auto=compress&cs=tinysrgb&w=900&h=675&fit=crop",
  },
  {
    title: "Parboiled Rice",
    items: ["Long grain", "Nutrient rich", "Bulk catering", "Food service"],
    img: "https://images.pexels.com/photos/6644072/pexels-photo-6644072.jpeg?auto=compress&cs=tinysrgb&w=900&h=675&fit=crop",
  },
];

const COUNTRIES = [
  "USA", "Germany", "UK", "UAE", "Saudi Arabia", "Japan", "Korea",
  "Australia", "France", "Netherlands", "Canada", "Italy", "Spain", "Malaysia",
];

export default function Home() {
  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-end overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMG})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0909] via-[#0A0909]/65 to-[#0A0909]/30" />
        <div className="relative max-w-[1480px] mx-auto px-6 md:px-10 pb-20 md:pb-28 w-full">
          <div className="overline fade-in">Punjab · Pakistan — Home of the World's Finest Basmati</div>
          <h1 className="fade-in fade-d1 mt-6 font-serif font-light tracking-[-0.02em] text-foreground text-[clamp(3rem,9vw,9.5rem)] leading-[0.92]">
            <span className="text-[#3FD97A]">ق</span> Group<span className="text-[#3FD97A]">.</span>
            <br />
            <span className="text-foreground/85">Premium Pakistani Rice,</span>
            <br />
            <span className="italic text-foreground/70 text-[0.55em]">"from the fields that feed nations."</span>
          </h1>
          <div className="fade-in fade-d2 mt-10 max-w-xl text-foreground/80 text-base md:text-lg leading-relaxed">
            From the fertile plains of Punjab, we export premium Basmati and specialty rice to markets worldwide. Direct from farm to port, with full traceability and certification.
          </div>
          <div className="fade-in fade-d3 mt-10 flex flex-col sm:flex-row gap-3">
            <Link
              to="/rfq"
              data-testid="hero-rfq-btn"
              className="inline-flex items-center justify-center gap-2 bg-[#3FD97A] hover:bg-[#4FE98A] text-[#0A0909] px-7 py-4 font-mono text-[11px] uppercase tracking-[0.22em] hover-shift font-semibold"
            >
              Start an order <ArrowUpRight size={16} />
            </Link>
            <Link
              to="/products"
              data-testid="hero-products-btn"
              className="inline-flex items-center justify-center gap-2 border border-border hover:border-[#3FD97A] text-foreground px-7 py-4 font-mono text-[11px] uppercase tracking-[0.22em] hover-shift"
            >
              View catalogue <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="border-y border-border bg-[#0A0909]">
        <div className="max-w-[1480px] mx-auto grid grid-cols-2 md:grid-cols-4">
          {STATS.map((s, i) => (
            <div key={i} className={`p-8 md:p-10 ${i !== 0 ? "border-l border-border" : ""} ${i % 2 !== 0 ? "border-l border-border" : ""}`}>
              <div className="overline">0{i + 1}</div>
              <div className="font-serif text-5xl md:text-6xl mt-3 text-[#F4F1EB]">{s.k}</div>
              <div className="mt-1 text-sm text-muted-foreground">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* MISSION */}
      <section className="py-28 md:py-36">
        <div className="max-w-[1480px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="overline">Our Mandate</div>
            <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">// 01 — Source</p>
            <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">// 02 — Quality</p>
            <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">// 03 — Export</p>
          </div>
          <div className="md:col-span-8">
            <h2 className="font-serif text-4xl md:text-6xl leading-[1.02] tracking-tight">
              Agricultural excellence from field to global market.
            </h2>
            <p className="mt-8 text-foreground/75 text-base md:text-lg leading-relaxed max-w-3xl">
              Qaaf Group is built on one belief — that rice deserves the same
              rigour as fine commodities. We do not blend, broker or trade through middlemen. 
              Every container we ship comes{" "}
              <Link to="/about" className="text-[#3FD97A] italic hover:underline">
              straight from partner farms</Link> with full traceability.
            </p>
            <Link
              to="/about"
              data-testid="mission-about-link"
              className="mt-10 inline-flex items-center gap-2 text-[#3FD97A] hover:text-[#4FE98A] font-mono text-[11px] uppercase tracking-[0.22em]"
            >
              Read the full story <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="border-t border-border">
        <div className="max-w-[1480px] mx-auto px-6 md:px-10 py-20">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
              <div className="overline">Catalogue · 04 rice varieties</div>
              <h2 className="mt-4 font-serif text-4xl md:text-6xl tracking-tight">
                Three categories.<br />Endless quality.
              </h2>
            </div>
            <Link
              to="/products"
              data-testid="cat-view-all"
              className="inline-flex items-center gap-2 border border-border hover:border-[#3FD97A] px-5 py-3 font-mono text-[11px] uppercase tracking-[0.22em] hover-shift self-start"
            >
              All products <ArrowUpRight size={14} />
            </Link>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-px bg-border">
            {CATEGORIES.map((c, i) => (
              <Link
                to="/products"
                key={i}
                data-testid={`category-card-${i}`}
                className="group bg-[#0A0909] p-8 md:p-10 hover:bg-[#141212] transition-colors"
              >
                <div className="aspect-[4/3] w-full overflow-hidden bg-[#141212] mb-8">
                  <img src={c.img} alt={c.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="overline">0{i + 1} / 03</div>
                <h3 className="mt-3 font-serif text-3xl md:text-4xl tracking-tight">{c.title}</h3>
                <ul className="mt-6 flex flex-wrap gap-2">
                  {c.items.map((it) => (
                    <li key={it} className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground border border-border px-3 py-1">
                      {it}
                    </li>
                  ))}
                </ul>
                <div className="mt-8 inline-flex items-center gap-2 text-[#3FD97A] font-mono text-[11px] uppercase tracking-[0.22em]">
                  Explore <ArrowRight size={14} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CROP CALENDAR */}
      <CropCalendar />

      {/* COUNTRIES MARQUEE */}
      <section className="border-y border-border py-10 overflow-hidden bg-[#141212]">
        <div className="overline px-6 md:px-10 max-w-[1480px] mx-auto">Currently shipping to</div>
        <div className="mt-6 flex whitespace-nowrap marquee-animate">
          {[...COUNTRIES, ...COUNTRIES, ...COUNTRIES].map((c, i) => (
            <span key={i} className="font-serif text-3xl md:text-5xl text-foreground/40 mx-8">
              {c} <span className="text-[#3FD97A]">·</span>
            </span>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-28 md:py-36">
        <div className="max-w-[1480px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-7">
            <div className="overline">Next Step</div>
            <h2 className="mt-6 font-serif text-4xl md:text-7xl tracking-tight leading-[1]">
              Tell us your<br /> port. We'll quote.
            </h2>
          </div>
          <div className="md:col-span-5">
            <p className="text-foreground/75 text-base md:text-lg leading-relaxed">
              Send us a brief on your product requirements, grade specifications, packaging needs, target volume and destination port. Our export desk responds within one business day with documentation and a CIF/FOB quotation.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link
                to="/rfq"
                data-testid="cta-rfq-btn"
                className="inline-flex items-center justify-center gap-2 bg-[#3FD97A] hover:bg-[#4FE98A] text-[#0A0909] px-7 py-4 font-mono text-[11px] uppercase tracking-[0.22em] hover-shift font-semibold"
              >
                Submit RFQ <ArrowUpRight size={16} />
              </Link>
              <Link
                to="/contact"
                data-testid="cta-contact-btn"
                className="inline-flex items-center justify-center gap-2 border border-border hover:border-[#3FD97A] px-7 py-4 font-mono text-[11px] uppercase tracking-[0.22em] hover-shift"
              >
                General enquiry <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
